import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client/react';
import '../Settings.scss';
import { COUNT_BY_DIMENSIONS, PRODUCT_DATA } from '../../../services/queries.js';
import countriesDictionary from '../../../config/countriesDictionary.js';
import DataTable from './DataTable.jsx';

const EMPTY_SECTOR_LABEL = '—';

const RECORD_COLS = [
    { label: 'ID',        key: 'id' },
    { label: 'Product',   key: 'product' },
    { label: 'Sector',    key: 'sector' },
    { label: 'Activity',  key: 'activity' },
    { label: 'Country',   key: 'country' },
    { label: 'Subregion', key: 'subregion' },
    { label: 'Year',      key: 'year' },
    { label: 'Month',     key: 'month' },
    { label: 'Status',    key: 'status' },
    { label: 'Value',     key: 'value',    right: true,
      fmt: v => v != null ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '' },
    { label: 'File',      key: 'fileName' },
    { label: 'Timestamp', key: '_ts',
      fmt: v => v ? new Date(Number(v) * 1000).toLocaleString() : '' },
];

const DrillPanel = ({ header, children, wide }) => (
    <div className={`drilldown-panel${wide ? ' drilldown-panel--wide' : ''}`}>
        <div className="drilldown-panel-header">{header}</div>
        <div className="drilldown-panel-body">{children}</div>
    </div>
);

const aggregate = (rows, key) => {
    const map = {};
    rows.forEach(r => {
        const k = r[key] || '';
        map[k] = (map[k] || 0) + Number(r.count);
    });
    return Object.entries(map)
        .map(([value, total]) => ({ value, total }))
        .sort((a, b) => b.total - a.total);
};

const ProductDrilldown = () => {
    const { data, loading, error } = useQuery(COUNT_BY_DIMENSIONS, {
        fetchPolicy: 'cache-and-network',
    });

    const [fetchRecords, { data: recordsData, loading: recordsLoading, error: recordsError }] = useLazyQuery(PRODUCT_DATA, {
        fetchPolicy: 'cache-and-network',
    });

    const [selectedProduct,   setSelectedProduct]   = useState(null);
    const [selectedSector,    setSelectedSector]    = useState(null);
    const [selectedActivity,  setSelectedActivity]  = useState(null);
    const [selectedCountry,   setSelectedCountry]   = useState(null);
    const [selectedSubregion, setSelectedSubregion] = useState(null);

    const rows = useMemo(() => data?.countByDimensions || [], [data]);

    // Panel 1: Products
    const productTotals = useMemo(() => aggregate(rows, 'product'), [rows]);

    // Panel 2: Sectors for selected product
    const sectorsForProduct = useMemo(() => {
        if (!selectedProduct) return [];
        return aggregate(rows.filter(r => r.product === selectedProduct), 'sector');
    }, [rows, selectedProduct]);

    // Panel 3: Activities for selected product + sector
    const activitiesForProductSector = useMemo(() => {
        if (!selectedProduct || selectedSector === null) return [];
        return aggregate(
            rows.filter(r => r.product === selectedProduct && (r.sector || '') === selectedSector),
            'activity'
        );
    }, [rows, selectedProduct, selectedSector]);

    // Panel 4: Countries — aggregate across subregions
    const countryRows = useMemo(() => {
        if (!selectedProduct || selectedSector === null || !selectedActivity) return [];
        return aggregate(
            rows.filter(r =>
                r.product === selectedProduct &&
                (r.sector || '') === selectedSector &&
                r.activity === selectedActivity
            ),
            'country'
        );
    }, [rows, selectedProduct, selectedSector, selectedActivity]);

    // Panel 5: Subregions — only rows with a non-empty subregion for the selected country
    const subregionRows = useMemo(() => {
        if (!selectedProduct || selectedSector === null || !selectedActivity || !selectedCountry) return [];
        const matching = rows.filter(r =>
            r.product === selectedProduct &&
            (r.sector || '') === selectedSector &&
            r.activity === selectedActivity &&
            r.country === selectedCountry
        );
        const withSubregion = matching
            .filter(r => r.subregion)
            .map(r => ({ value: r.subregion, total: Number(r.count) }))
            .sort((a, b) => b.total - a.total);
        const noSubregionTotal = matching
            .filter(r => !r.subregion)
            .reduce((sum, r) => sum + Number(r.count), 0);
        if (noSubregionTotal > 0) {
            withSubregion.push({ value: '', total: noSubregionTotal });
        }
        return withSubregion;
    }, [rows, selectedProduct, selectedSector, selectedActivity, selectedCountry]);

    // Fetch records when a subregion is selected
    useEffect(() => {
        if (selectedSubregion === null) return;
        const productVar = selectedSector
            ? [{ product: selectedProduct, sector: selectedSector }]
            : [{ product: selectedProduct }];
        fetchRecords({
            variables: {
                outlookId: 'qa',
                product:   productVar,
                activity:  [selectedActivity],
                subregion: [{ country: selectedCountry, subregion: selectedSubregion }],
            },
        });
    }, [selectedSubregion, selectedProduct, selectedSector, selectedActivity, selectedCountry, fetchRecords]);

    const handleProductClick = (product) => {
        setSelectedProduct(product);
        setSelectedSector(null);
        setSelectedActivity(null);
        setSelectedCountry(null);
        setSelectedSubregion(null);
    };

    const handleSectorClick = (sector) => {
        setSelectedSector(sector);
        setSelectedActivity(null);
        setSelectedCountry(null);
        setSelectedSubregion(null);
    };

    const handleActivityClick = (activity) => {
        setSelectedActivity(activity);
        setSelectedCountry(null);
        setSelectedSubregion(null);
    };

    const handleCountryClick = (country) => {
        setSelectedCountry(country);
        setSelectedSubregion(null);
    };

    if (error) return <div className="error">Error loading data: {error.message}</div>;

    const showSubregions = selectedCountry && subregionRows.length > 0;
    const records        = recordsData?.productData?.data ?? [];
    const meta           = recordsData?.productData?.metaData;

    return (
        <div className="settings-section">
            <h3>Product Drill-Down</h3>
            <p>Select a product, then a sector, then an activity to see the country breakdown.</p>

            {loading && <div className="settings-loading">Loading counts...</div>}

            <div className="drilldown-layout">
                {/* Panel 1: Products */}
                <DrillPanel header="Products">
                    {productTotals.map(({ value, total }) => (
                        <div
                            key={value}
                            className={`drilldown-item${selectedProduct === value ? ' selected' : ''}`}
                            onClick={() => handleProductClick(value)}
                        >
                            <span>{value}</span>
                            <span className="drilldown-badge">{total.toLocaleString()}</span>
                        </div>
                    ))}
                </DrillPanel>

                {/* Panel 2: Sectors */}
                <DrillPanel header={selectedProduct ? `Sectors — ${selectedProduct}` : 'Sectors'}>
                    {!selectedProduct
                        ? <div className="drilldown-placeholder">Select a product</div>
                        : sectorsForProduct.map(({ value, total }) => (
                            <div
                                key={value}
                                className={`drilldown-item${selectedSector === value ? ' selected' : ''}`}
                                onClick={() => handleSectorClick(value)}
                            >
                                <span>{value || EMPTY_SECTOR_LABEL}</span>
                                <span className="drilldown-badge">{total.toLocaleString()}</span>
                            </div>
                        ))
                    }
                </DrillPanel>

                {/* Panel 3: Activities */}
                <DrillPanel header={selectedSector !== null ? `Activities — ${selectedSector || EMPTY_SECTOR_LABEL}` : 'Activities'}>
                    {selectedSector === null
                        ? <div className="drilldown-placeholder">Select a sector</div>
                        : activitiesForProductSector.map(({ value, total }) => (
                            <div
                                key={value}
                                className={`drilldown-item${selectedActivity === value ? ' selected' : ''}`}
                                onClick={() => handleActivityClick(value)}
                            >
                                <span>{value}</span>
                                <span className="drilldown-badge">{total.toLocaleString()}</span>
                            </div>
                        ))
                    }
                </DrillPanel>

                {/* Panel 4: Countries */}
                <DrillPanel
                    header={selectedActivity ? `Countries — ${selectedActivity}` : 'Countries'}
                    wide={!showSubregions}
                >
                    {!selectedActivity
                        ? <div className="drilldown-placeholder">Select an activity</div>
                        : countryRows.map(({ value, total }) => (
                            <div
                                key={value}
                                className={`drilldown-item${selectedCountry === value ? ' selected' : ''}`}
                                onClick={() => handleCountryClick(value)}
                            >
                                <span>
                                    <code className="drilldown-code">{value}</code>
                                    {' '}
                                    {countriesDictionary[value]?.label ?? ''}
                                </span>
                                <span className="drilldown-badge">{total.toLocaleString()}</span>
                            </div>
                        ))
                    }
                </DrillPanel>

                {/* Panel 5: Subregions — only when the selected country has subregion data */}
                {showSubregions && (
                    <DrillPanel header={`Subregions — ${selectedCountry}`} wide>
                        {subregionRows.map(({ value, total }) => (
                            <div
                                key={value || '__none__'}
                                className={`drilldown-item${selectedSubregion === value ? ' selected' : ''}`}
                                onClick={() => setSelectedSubregion(value)}
                            >
                                <span>{value || <em>No subregion</em>}</span>
                                <span className="drilldown-badge">{total.toLocaleString()}</span>
                            </div>
                        ))}
                    </DrillPanel>
                )}
            </div>

            {/* Records table — shown when a subregion is selected */}
            {selectedSubregion !== null && (
                <div className="drilldown-records">
                    <h4 className="drilldown-records-header">
                        {[selectedProduct, selectedSector || EMPTY_SECTOR_LABEL, selectedActivity, selectedCountry, selectedSubregion || 'No subregion']
                            .join(' › ')}
                    </h4>

                    {meta && (
                        <p className="drilldown-records-meta">
                            {meta.totalCount?.toLocaleString()} records
                            {meta.hasMoreResults && ' — showing first page only'}
                        </p>
                    )}

                    {recordsLoading && <div className="settings-loading">Loading records...</div>}

                    {recordsError && (
                        <div style={{ color: 'red', marginBottom: '1rem' }}>
                            Error loading records: {recordsError.message}
                        </div>
                    )}

                    {!recordsLoading && !recordsError && records.length === 0 && recordsData && (
                        <div className="drilldown-placeholder">No records found.</div>
                    )}

                    {!recordsLoading && records.length > 0 && (
                        <DataTable cols={RECORD_COLS} rows={records} />
                    )}
                </div>
            )}
        </div>
    );
};

export default ProductDrilldown;
